import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';
import { ManagerService } from '@ddv/layout';
import { DateRangeString, DdvDate, FastnessResponse, resolvePotentialCcFuzzyDate, toDefaultQueryParams } from '@ddv/models';
import { FuzzyDate } from '@hs/ui-core-date';
import { ConfirmationService } from '@hs/ui-core-notifications';
import { Subscription } from 'rxjs';

import { DataFastnessService } from '../../services/data-fastness.service';
import { SlowDataApprovalService } from '../../services/slow-data-approval.service';

@Component({
    selector: 'ddv-slow-data-approval',
    templateUrl: './slow-data-approval.component.html',
    styleUrls: ['./slow-data-approval.component.scss'],
})
export class SlowDataApprovalComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input() clientCode: string | undefined;
    @Input() selectedDate: string | DateRangeString | undefined;
    @Input() queryType = 'Recon';
    @Input() fuzzyDates: FuzzyDate[] = [];

    @Output() approved: EventEmitter<boolean> = new EventEmitter();

    private dateRange: { dateFrom: string, dateTo: string } | undefined;
    private checkSubscription: Subscription | undefined;

    constructor(
        private readonly fastnessService: DataFastnessService,
        private readonly approvalService: SlowDataApprovalService,
        private readonly confirmationService: ConfirmationService,
        // this is only needed to get "default" dates
        // given the way pulling out this component has gone, this actually feels unnecessary
        // it feels like the parent component should be responsible for providing the dates even if they are the defaults
        private readonly managerService: ManagerService,
    ) {}

    @ViewChild('template', { static: true }) private readonly template: TemplateRef<unknown> | undefined;

    ngOnChanges(): void {
        this.dateRange = this.getDatesForSlowDataCheck(this.selectedDate);
        this.checkInputsAndProceed();
    }

    ngAfterViewInit(): void {
        this.checkInputsAndProceed();
    }

    ngOnDestroy(): void {
        this.checkSubscription?.unsubscribe();
    }

    private checkInputsAndProceed(): void {
        if (!this.dateRange || !this.clientCode || !this.template || !this.fuzzyDates.length) {
            return;
        }

        // capture values in a closure to avoid rechecking undefined in the subscribe block
        const clientCode = this.clientCode;
        const template = this.template;
        const dateTo = this.dateRange.dateTo;
        const resolveToDate = resolvePotentialCcFuzzyDate(this.fuzzyDates, dateTo);
        const queryType = this.queryType === 'Recon' ? 'rc2' : this.queryType;

        this.checkSubscription?.unsubscribe();
        this.checkSubscription = this.fastnessService.checkFastness(clientCode, this.dateRange.dateFrom, dateTo, queryType, this.fuzzyDates)
            .subscribe({
                next: (result: FastnessResponse) => {
                    const toDateKey = DdvDate.fromUSFormat(resolveToDate).toReversePaddedDashFormat();
                    const resultForToDate = result[queryType][toDateKey];

                    if (resultForToDate && !resultForToDate.fast) {
                        // this happens because this component is used both on:
                        //   - the dashboard-filters (where dates are changed)
                        //   - the workspace (where dates are "used" to query)
                        // there is a race condition here were after you approve slow data from the filters,
                        // it then becomes "the date" so the workspace component re-renders this component
                        // and at that time, its still "being cached" so is still slow.  so we need to see if already been ok'd that its slow
                        if (this.approvalService.isApproved(clientCode, resolveToDate, this.queryType)) {
                            return this.approved.emit(true);
                        }

                        this.getConfirmationToProceedWithSlowData(clientCode, resolveToDate, template);
                    } else {
                        this.approvalService.approve(clientCode, resolveToDate, this.queryType);
                        this.approved.emit(true);
                    }
                },
            });
    }

    private getDatesForSlowDataCheck(selectedDate: string | DateRangeString | undefined): { dateFrom: string, dateTo: string } {
        if (typeof selectedDate === 'string') {
            return {
                dateFrom: selectedDate,
                dateTo: selectedDate,
            };
        }

        if (selectedDate) {
            return {
                dateFrom: selectedDate.dateFrom ?? '',
                dateTo: selectedDate.dateTo ?? '',
            };
        }

        const hsDefaults = toDefaultQueryParams([], [], this.managerService.getExtraParametersForWorkspace());
        return {
            dateFrom: hsDefaults.startDate ?? '',
            dateTo: hsDefaults.endDate ?? '',
        };
    }

    private getConfirmationToProceedWithSlowData(
        clientCode: string,
        dateTo: string,
        template: TemplateRef<unknown>,
    ): void {
        this.confirmationService.launch(template, { confirmButtonText: 'OK', denyButtonText: 'Cancel' })
            .subscribe({
                next: (action) => {
                    if (action === 'confirm') {
                        this.approvalService.approve(clientCode, dateTo, this.queryType);
                        this.approved.emit(true);
                    } else {
                        this.approvalService.deny(clientCode, dateTo, this.queryType);
                        this.approved.emit(false);
                    }
                },
            });
    }
}
